import { createStore, applyMiddleware, compose } from 'redux';
import { persistStore, persistReducer } from 'redux-persist';
import thunk from 'redux-thunk';
import localforage from 'localforage';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Imports: Redux
import rootReducer from '@reducers';
import { isWeb } from '@helpers';
import { messageHandlerMiddleware } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { messagesBusCleanupMiddleware } from '@reducers/v2/messagesBus/middleware/messagesBusCleanupMiddleware';
import { messageBusDispatchMiddleware } from '@reducers/v2/messagesBus/middleware/messageBusDispatchMiddleware';

import { getPersistUserEmail } from './persistUserScope';

// Per-user scoped storage for web: each account gets its own persist key in IndexedDB.
// When email is unknown ('default'), persistence is disabled with no reads, no writes.
// this prevents the 'persist:root:default' bucket from sharing across accounts (since threated as default).
// once the email is set after login/account switch, persistence activates for the correct user and assigned to their email.
const userScopedStorage = {
  getItem: (key) => {
    const email = getPersistUserEmail();
    if (email === 'default') return Promise.resolve(null);
    const scopedKey = `${key}:${email}`;
    return localforage.getItem(scopedKey);
  },
  setItem: (key, value) => {
    const email = getPersistUserEmail();
    if (email === 'default') return Promise.resolve();
    const scopedKey = `${key}:${email}`;
    return localforage.setItem(scopedKey, value);
  },
  removeItem: (key) => {
    const email = getPersistUserEmail();
    if (email === 'default') return Promise.resolve();
    return localforage.removeItem(`${key}:${email}`);
  },
};

// Middleware: Redux Persist Config
const persistConfig = {
  key: 'root',
  // Web: per-user scoped storage (IndexedDB). Native: AsyncStorage.
  storage: isWeb ? userScopedStorage : AsyncStorage,
  timeout: 0,
  blacklist: [
    'blobStorage',
    'list',
    'imageHandling',
    'signalR',
    'device',
    'productionOrders',
    // 'membership',
    'manufacture',
    'images',
    'parts',
    'fileConnector',
    'onBoarding',
    'onboardingWizard',
    'dynamicConfig',
    'machinesV2',
    'header',
    'params',
    'batch',
    'details',
    'notification',
    'operatorPanel',
    'retrieveQueue',
    'imageHandlingV2',
    'annotationsV2',
    'planboard',
    'tableAccordion',
    'bottomSheet',
    'dashboard',
    'connector',
  ],
};

// Middleware: Redux Persist Persisted Reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Redux: Store
const store = createStore(
  persistedReducer,
  applyMiddleware(thunk, messageBusDispatchMiddleware, messageHandlerMiddleware, messagesBusCleanupMiddleware)
  // compose(applyMiddleware(thunk, messageHandlerMiddleware), window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__())
);

// Middleware: Redux Persist Persister
let persistor = persistStore(store);

// Exports
export default { store, persistor };
